// Slice 11 — Leaderboards.
import { useEffect, useMemo, useState } from "react";
import { Trophy, Loader2, RefreshCw, Download, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { RangeControl, rangeParams, isoToday, isoDaysAgo } from "./_daterange";

const METRICS = [
  { id: "calls",       label: "Most Calls Handled",  unit: "" },
  { id: "talk_time",   label: "Most Talk Time",      unit: "min" },
  { id: "answer_rate", label: "Best Answer Rate",    unit: "%" },
  { id: "aht",         label: "Lowest AHT",          unit: "s" },
];

function fmtMetric(row, metric) {
  if (metric === "talk_time") return `${Math.round(row.talk_time / 60)}m`;
  if (metric === "answer_rate") return `${row.answer_rate}%`;
  if (metric === "aht") return `${row.aht}s`;
  return Intl.NumberFormat().format(row.calls);
}

function rankBadge(rank) {
  if (rank === 1) return "bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-300";
  if (rank === 2) return "bg-slate-200 dark:bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-300";
  if (rank === 3) return "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-300";
  return "bg-muted text-muted-foreground border-border";
}

export default function LeaderboardsPage() {
  const [metric, setMetric] = useState("calls");
  const [rng, setRng] = useState("today");
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (rng === "custom" && !(from && to)) return;
    setBusy(true);
    try {
      const p = new URLSearchParams({ metric, limit: 25, ...rangeParams(rng, from, to) });
      const r = await api.get(`/reports/leaderboard?${p.toString()}`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [metric, rng, from, to]);

  function exportCsv() {
    if (!rows.length) return;
    const hdr = ["Rank", "Agent", "Calls", "Answered", "Talk time (s)", "AHT (s)", "Answer rate (%)"];
    const csv = [hdr.join(",")].concat(rows.map((r) =>
      [r.rank, `"${r.agent_name}"`, r.calls, r.answered, r.talk_time, r.aht, r.answer_rate].join(",")
    )).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `leaderboard-${metric}-${rng}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  }

  const top = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-5xl" data-testid="leaderboards-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Agent Leaderboards</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rank your team by handled volume, talk time, AHT or answer rate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-56" data-testid="lb-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <RangeControl rng={rng} setRng={setRng} from={from} to={to} setFrom={setFrom} setTo={setTo} testid="lb-range" />
          <Button variant="outline" onClick={load} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
          </Button>
          <Button onClick={exportCsv} disabled={!rows.length} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="lb-export">
            <Download className="w-4 h-4"/> CSV
          </Button>
        </div>
      </div>

      {/* Podium */}
      {top.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 mb-5" data-testid="lb-podium">
          {top.map((r) => (
            <Card key={r.agent_id} className="p-5 text-center relative overflow-hidden" data-testid={`lb-podium-${r.rank}`}>
              {r.rank === 1 && <Crown className="absolute top-3 right-3 w-5 h-5 text-yellow-500"/>}
              <Badge className={`text-xs font-bold border ${rankBadge(r.rank)}`}>Rank #{r.rank}</Badge>
              <div className="font-display font-extrabold text-2xl mt-3 truncate">{r.agent_name}</div>
              <div className="font-display font-extrabold text-4xl text-primary mt-2">{fmtMetric(r, metric)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {r.calls} calls · {r.answered} answered · AHT {r.aht}s
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden" data-testid="lb-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider w-16">Rank</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Agent</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Calls</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Answered</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Talk</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">AHT</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Answer rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No agent activity in this range.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.agent_id} className="border-t border-border/60 hover:bg-muted/20">
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`text-xs font-bold ${rankBadge(r.rank)}`}>#{r.rank}</Badge>
                </td>
                <td className="px-3 py-2 font-medium">{r.agent_name}</td>
                <td className="px-3 py-2 text-right font-mono">{r.calls}</td>
                <td className="px-3 py-2 text-right font-mono">{r.answered}</td>
                <td className="px-3 py-2 text-right font-mono">{Math.round(r.talk_time / 60)}m</td>
                <td className="px-3 py-2 text-right font-mono">{r.aht}s</td>
                <td className="px-3 py-2 text-right font-mono">{r.answer_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
